import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import StoreOutletSelector from '../store/StoreOutletSelector';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  PointOfSale,
  Inventory,
  People,
  Receipt,
  Assessment,
  AccountBalance,
  Settings,
  Store,
  ManageAccounts,
  Security,
  FactCheck,
  SwapHoriz,
  ExpandLess,
  ExpandMore,
  ShowChart,
  CompareArrows,
  AccountBalanceWallet,
  ShoppingCart,
  LocalOffer,
} from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';
import { useHasAccess } from '@/hooks/useHasAccess';
import { useSettings } from '@/hooks/useSettings';
import { getLogoUrl } from '@/utils/imageUrl';
import { selectStoreAndOutlet } from '@/store/slices/storeSlice';

interface SidebarProps {
  onItemClick?: () => void;
}

interface NavItem {
  titleKey: string;
  path: string | ((storeId?: string) => string);
  icon: React.ReactNode;
  permissions?: string[];
  divider?: boolean;
  requiresStore?: boolean;
  /** Minimum store role required to see the item (OWNER > MANAGER > STAFF). */
  requireRole?: 'STAFF' | 'MANAGER' | 'OWNER';
  children?: NavItem[];
}

const getNavItems = (): NavItem[] => [
  // Operational Flow (Daily Tasks)
  {
    titleKey: 'common:navigation.dashboard',
    path: '/dashboard',
    icon: <Dashboard />,
  },
  {
    titleKey: 'common:navigation.pos',
    path: '/pos',
    icon: <PointOfSale />,
    permissions: ['pos.create_transaction'],
  },
  {
    titleKey: 'common:navigation.transactions',
    path: '/transactions',
    icon: <Receipt />,
    permissions: ['pos.view_transaction'],
    divider: true,
  },
  // Inventory & Customer Management
  {
    titleKey: 'common:navigation.inventory',
    path: '/inventory',
    icon: <Inventory />,
    permissions: ['inventory.view'],
  },
  {
    titleKey: 'common:navigation.stockTransfer',
    path: '/inventory/stock-transfer',
    icon: <SwapHoriz />,
    permissions: ['inventory.transfer_stock'],
  },
  {
    titleKey: 'common:navigation.stockAudit',
    path: '/stock-audit',
    icon: <FactCheck />,
    permissions: ['stock_audit.view'],
  },
  {
    titleKey: 'common:navigation.purchaseOrders',
    path: '/purchase-orders',
    icon: <ShoppingCart />,
    permissions: ['purchase_orders.view'],
  },
  {
    titleKey: 'common:navigation.promotions',
    path: '/promotions',
    icon: <LocalOffer />,
    permissions: ['promotions.view'],
  },
  {
    titleKey: 'common:navigation.customers',
    path: '/customers',
    icon: <People />,
    permissions: ['customers.view'],
    divider: true,
  },
  // Business Intelligence
  {
    titleKey: 'common:navigation.reports',
    path: '/reports',
    icon: <Assessment />,
    permissions: ['reports.view_sales', 'reports.view_inventory', 'reports.view_customers', 'reports.view_financial', 'stock_audit.view'],
    children: [
      {
        titleKey: 'reports:sidebar.sales',
        path: '/reports',
        icon: <ShowChart />,
        permissions: ['reports.view_sales', 'reports.view_inventory', 'reports.view_customers', 'reports.view_financial', 'stock_audit.view'],
      },
      {
        titleKey: 'reports:sidebar.multi_outlet',
        path: '/reports/multi-outlet',
        icon: <Store />,
        permissions: ['reports.view_sales'],
        requireRole: 'MANAGER',
      },
      {
        titleKey: 'reports:sidebar.outlet_comparison',
        path: '/reports/outlet-comparison',
        icon: <CompareArrows />,
        permissions: ['reports.view_sales'],
        requireRole: 'MANAGER',
      },
      {
        titleKey: 'reports:sidebar.store_financial',
        path: '/reports/store-financial',
        icon: <AccountBalanceWallet />,
        permissions: ['reports.view_financial'],
      },
    ],
  },
  {
    titleKey: 'common:navigation.expenses',
    path: '/expenses',
    icon: <AccountBalance />,
    permissions: ['expenses.view'],
  },
  {
    titleKey: 'common:navigation.store_cash',
    path: '/outlet-cash',
    icon: <Store />,
    permissions: ['cash.view_outlet_cash'],
    divider: true,
  },
  // Administration

  {
    titleKey: 'common:navigation.users',
    path: '/users',
    icon: <ManageAccounts />,
    permissions: ['users.view'],
  },
  {
    titleKey: 'common:navigation.audit',
    path: '/audit',
    icon: <Security />,
    permissions: ['audit.view_logs'],
  },
  {
    titleKey: 'common:navigation.settings',
    path: '/settings',
    icon: <Settings />,
    permissions: ['system.manage_settings', 'system.view_store', 'system.manage_backup'],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ onItemClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAnyPermission, user } = useAuth();
  const { hasCurrentStoreRole } = useHasAccess();
  const { settings } = useSettings();
  const { t } = useTranslation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  // Get store and outlet from Redux state (storeSlice as source of truth)
  const { storeId: currentStoreId, storeName } = useSelector(selectStoreAndOutlet);

  const navItems = getNavItems();

  const toggleMenu = (titleKey: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [titleKey]: !prev[titleKey],
    }));
  };

  const getItemPath = (item: NavItem): string => {
    if (typeof item.path === 'function') {
      return item.path(currentStoreId || undefined);
    }
    return item.path;
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const isItemVisible = (item: NavItem): boolean => {
    // Hide items that require store if no store is selected
    if (item.requiresStore && !currentStoreId) {
      return false;
    }

    // Hide multi-outlet specific menus if only one outlet is available
    const multiOutletKeys = [
      'reports:sidebar.multi_outlet',
      'reports:sidebar.outlet_comparison'
    ];

    // Calculate visible outlets for the current store
    const currentStoreOutlets = user?.outlets?.filter(o => o.storeId === currentStoreId) || [];
    const hasMultipleOutlets = currentStoreOutlets.length > 1;

    if (multiOutletKeys.includes(item.titleKey) && !hasMultipleOutlets) {
      return false;
    }

    if (item.requireRole && !hasCurrentStoreRole(item.requireRole)) {
      return false;
    }

    if (!item.permissions || item.permissions.length === 0) {
      return true;
    }
    return hasAnyPermission(item.permissions);
  };

  const isItemActive = (path: string): boolean => {
    if (path === '/dashboard' && location.pathname === '/') {
      return true;
    }
    return location.pathname === path;
  };

  const isParentActive = (item: NavItem): boolean => {
    if (!item.children) return false;
    return item.children.some(child => {
      const childPath = getItemPath(child);
      return location.pathname === childPath;
    });
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
{settings?.logoUrl ? (
          <Box
            component="img"
            src={getLogoUrl(settings.logoUrl)}
            alt={storeName || 'Store Logo'}
            sx={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '50%',
              objectFit: 'cover',
              mr: 2,
              border: '2px solid',
              borderColor: 'primary.main',
            }}
            onError={(e) => {
              // Fallback to default icon if image fails to load
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.style.cssText = `
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 40px;
                  height: 40px;
                  border-radius: 50%;
                  background: linear-gradient(135deg, #1976d2, #42a5f5);
                  margin-right: 16px;
                `;
                fallback.innerHTML = '<svg style="color: white; font-size: 24px;" viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.11,4 20,4Z"/></svg>';
                parent.appendChild(fallback);
              }
            }}
          />
        ) : (
          <Box
            component="img"
            src="/assets/logo.svg"
            alt="Ankassa"
            sx={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '50%',
              objectFit: 'cover',
              mr: 2,
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          />
        )}
        <Box>
          <Typography variant="h6" fontWeight="bold" color="primary">
            {storeName || 'Ankassa'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            v1.0.0
          </Typography>
        </Box>
      </Box>

      {/* Mobile Store/Outlet Selector */}
      {isMobile && (
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiButton-root': { width: '100%', justifyContent: 'space-between' }
          }}
        >
          <StoreOutletSelector />
        </Box>
      )}

      {/* Navigation Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ px: 1, py: 2 }}>
          {navItems
            .filter(isItemVisible)
            .map((item) => {
              const itemPath = getItemPath(item);
              const itemKey = typeof item.path === 'function' ? item.titleKey : item.path;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedMenus[item.titleKey];
              const hasActiveChild = isParentActive(item);

              return (
                <React.Fragment key={itemKey}>
                  <ListItemButton
                    onClick={() => {
                      if (hasChildren) {
                        toggleMenu(item.titleKey);
                      } else {
                        handleItemClick(itemPath);
                      }
                    }}
                    selected={isItemActive(itemPath) || hasActiveChild}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      mx: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: hasActiveChild ? 'primary.light' : 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'primary.contrastText',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        '& .MuiListItemIcon-root': {
                          color: 'primary.contrastText',
                        },
                      },
                      transition: theme.transitions.create(['background-color', 'color'], {
                        duration: theme.transitions.duration.short,
                      }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: '2.5rem',
                        color: (isItemActive(itemPath) || hasActiveChild) ? 'inherit' : 'action.active',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={t(item.titleKey)}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: (isItemActive(itemPath) || hasActiveChild) ? 600 : 500,
                      }}
                    />
                    {hasChildren && (
                      isExpanded ? <ExpandLess /> : <ExpandMore />
                    )}
                  </ListItemButton>

                  {/* Nested Menu Items */}
                  {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children!
                          .filter(isItemVisible)
                          .map((child) => {
                            const childPath = getItemPath(child);
                            const childKey = typeof child.path === 'function' ? child.titleKey : child.path;

                            return (
                              <ListItemButton
                                key={childKey}
                                onClick={() => handleItemClick(childPath)}
                                selected={isItemActive(childPath)}
                                sx={{
                                  pl: 4,
                                  borderRadius: 2,
                                  mb: 0.5,
                                  mx: 0.5,
                                  '&.Mui-selected': {
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                      backgroundColor: 'primary.dark',
                                    },
                                    '& .MuiListItemIcon-root': {
                                      color: 'primary.contrastText',
                                    },
                                  },
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                  transition: theme.transitions.create(['background-color', 'color'], {
                                    duration: theme.transitions.duration.short,
                                  }),
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: '2.25rem',
                                    color: isItemActive(childPath) ? 'inherit' : 'action.active',
                                  }}
                                >
                                  {child.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={t(child.titleKey)}
                                  primaryTypographyProps={{
                                    fontSize: '0.8125rem',
                                    fontWeight: isItemActive(childPath) ? 600 : 500,
                                  }}
                                />
                              </ListItemButton>
                            );
                          })}
                      </List>
                    </Collapse>
                  )}

                  {item.divider && <Divider sx={{ my: 1, mx: 2 }} />}
                </React.Fragment>
              );
            })}
        </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          © 2025 Ankassa Digital
        </Typography>
      </Box>
    </Box>
  );
};

export default Sidebar;
